import {ApolloClient, ApolloQueryResult, gql} from '@apollo/client';
import {errorResponse, NetworkResult, successResponse} from './NetworkResult';
import {Crew, FullCrew} from '../Models/Crew';

import {CrewInvite} from '../Models/CrewInvite';

export class CrewsService {
    constructor(private client: ApolloClient<any>) {}

    public async all(): Promise<NetworkResult<FullCrew[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    query {
                        crews {
                            id
                            name
                            description
                            timezone
                            is_advanced
                            members {
                                user_id
                                crew_membership_id
                                is_me
                                first_name
                                last_name
                                has_accepted_invite
                                is_coach
                            }
                        }
                    }
                `,
            });
            return this.toResponse(apolloQueryResult);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async get(crewId: number): Promise<NetworkResult<FullCrew>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    query ($crewId: Int!) {
                        crew(id: $crewId) {
                            id
                            name
                            description
                            timezone
                            is_advanced
                            max_member_count
                            members {
                                user_id
                                crew_membership_id
                                is_me
                                first_name
                                last_name
                                has_accepted_invite
                                is_coach
                            }
                        }
                    }
                `,
                variables: {
                    crewId,
                },
            });
            return this.toSingleResponse(apolloQueryResult);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async removeMember(crewMembershipId: number): Promise<NetworkResult<any>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation ($crewMembershipId: Int!) {
                        removeMemberFromCrew(crew_membership_id: $crewMembershipId) {
                            left_at_iso8601
                        }
                    }
                `,
                variables: {
                    crewMembershipId,
                },
            });

            // Make sure to reset the entire cache after a mutation, it has too many unexpected side effects otherwise
            await this.client.resetStore();

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(apolloQueryResult.data.removeMemberFromCrew.left_at_iso8601);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async makeCoach(crewMembershipId: number): Promise<NetworkResult<any>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation ($crewMembershipId: Int!) {
                        makeCrewMemberCoach(crew_membership_id: $crewMembershipId) {
                            is_coach
                        }
                    }
                `,
                variables: {
                    crewMembershipId,
                },
            });

            // Make sure to reset the entire cache after a mutation, it has too many unexpected side effects otherwise
            await this.client.resetStore();

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(apolloQueryResult.data.makeCrewCoach.left_at_iso8601);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async unmakeCoach(crewMembershipId: number): Promise<NetworkResult<any>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation ($crewMembershipId: Int!) {
                        unmakeCrewMemberCoach(crew_membership_id: $crewMembershipId) {
                            is_coach
                        }
                    }
                `,
                variables: {
                    crewMembershipId,
                },
            });

            // Make sure to reset the entire cache after a mutation, it has too many unexpected side effects otherwise
            await this.client.resetStore();

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(apolloQueryResult.data.makeCrewCoach.left_at_iso8601);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async invites(): Promise<NetworkResult<CrewInvite[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    query {
                        crewInvites {
                            crew_membership_id
                            crew {
                                id
                                name
                                description
                                timezone
                            }
                        }
                    }
                `,
            });
            return this.toResponseInvites(apolloQueryResult);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async acceptInvite(crewMembershipId: number): Promise<NetworkResult<any>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation ($crewMembershipId: Int!) {
                        acceptCrewMembership(crew_membership_id: $crewMembershipId) {
                            id
                        }
                    }
                `,
                variables: {
                    crewMembershipId,
                },
            });

            // Make sure to reset the entire cache after a mutation, it has too many unexpected side effects otherwise
            await this.client.resetStore();

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(apolloQueryResult.data.acceptCrewMembership.id);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    private toResponse(apolloQueryResult: ApolloQueryResult<any>): NetworkResult<FullCrew[]> {
        if (apolloQueryResult.errors) {
            return errorResponse();
        }

        return successResponse(
            apolloQueryResult.data.crews.map((crewResponse: any) => {
                return this.mapFull(crewResponse);
            }),
        );
    }

    private toSingleResponse(apolloQueryResult: ApolloQueryResult<any>): NetworkResult<FullCrew> {
        if (apolloQueryResult.errors) {
            return errorResponse();
        }

        return successResponse(this.mapFull(apolloQueryResult.data.crew));
    }

    private map(crewResponse: any): Crew {
        return {
            id: crewResponse.id,
            name: crewResponse.name,
            description: crewResponse.description,
            timezone: crewResponse.timezone,
        };
    }

    private mapFull(crewResponse: any): FullCrew {
        return {
            id: parseInt(crewResponse.id, 10),
            name: crewResponse.name,
            description: crewResponse.description,
            timezone: crewResponse.timezone,
            isAdvanced: crewResponse.is_advanced,
            maxMemberCount: crewResponse.max_member_count,
            members: crewResponse.members.map((memberResponse: any) => {
                return {
                    userId: memberResponse.user_id === null ? null : parseInt(memberResponse.user_id, 10),
                    crewMembershipId: memberResponse.crew_membership_id,
                    isMe: memberResponse.is_me,
                    firstName: memberResponse.first_name,
                    lastName: memberResponse.last_name,
                    hasAcceptedInvite: memberResponse.has_accepted_invite,
                    isCoach: memberResponse.is_coach,
                };
            }),
        };
    }

    private toResponseInvites(apolloQueryResult: ApolloQueryResult<any>): NetworkResult<CrewInvite[]> {
        if (apolloQueryResult.errors) {
            return errorResponse();
        }

        return successResponse(
            apolloQueryResult.data.crewInvites.map((crewInviteResponse: any) => {
                return {
                    crewMembershipId: crewInviteResponse.crew_membership_id,
                    crew: this.map(crewInviteResponse.crew),
                };
            }),
        );
    }
}
